import React, { useState } from 'react'
import { Check, Loader2, Minus } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { BulkLibrary, LibraryBulkFilter } from '@genieartinc/schemas'

/** What is selected: every model matching the current filter, or explicit ids. */
export interface LibrarySelection {
  all: boolean
  ids: Set<string>
}

export const EMPTY_SELECTION: LibrarySelection = { all: false, ids: new Set() }

interface LibraryBulkBarProps {
  selection: LibrarySelection
  totalCount: number
  filter: LibraryBulkFilter
  onSelectionChange: (selection: LibrarySelection) => void
  onBulkUpdate: (body: BulkLibrary) => Promise<number>
}

export function LibraryBulkBar({ selection, totalCount, filter, onSelectionChange, onBulkUpdate }: LibraryBulkBarProps) {
  const [busy, setBusy] = useState<BulkLibrary['action'] | null>(null)
  const [message, setMessage] = useState<{ text: string; error?: boolean } | null>(null)

  const count = selection.all ? totalCount : selection.ids.size
  const state = selection.all ? 'all' : count > 0 ? 'some' : 'none'

  const run = async (action: BulkLibrary['action']) => {
    setBusy(action)
    setMessage(null)
    try {
      const body: BulkLibrary = selection.all ? { action, filter } : { action, modelIds: [...selection.ids] }
      const affected = await onBulkUpdate(body)
      setMessage({ text: `${action === 'add' ? 'Added' : 'Removed'} ${affected} ${affected === 1 ? 'model' : 'models'}` })
      onSelectionChange(EMPTY_SELECTION)
    } catch (error) {
      setMessage({ text: error instanceof Error ? error.message : 'Could not update your library', error: true })
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2 px-3 pb-3 flex-shrink-0">
      <button
        type="button"
        role="checkbox"
        aria-checked={state === 'all' ? true : state === 'some' ? 'mixed' : false}
        disabled={totalCount === 0}
        onClick={() => onSelectionChange(state === 'none' ? { all: true, ids: new Set() } : EMPTY_SELECTION)}
        className="flex h-8 items-center gap-2 rounded-lg px-2 text-xs text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50"
      >
        <SelectBox state={state} />
        {state === 'none' ? `Select all ${totalCount}` : `${count} selected`}
      </button>

      {state === 'some' && count < totalCount && (
        <button
          type="button"
          onClick={() => onSelectionChange({ all: true, ids: new Set() })}
          className="h-8 rounded-lg px-2 text-xs text-primary hover:underline"
        >
          Select all {totalCount}
        </button>
      )}

      <div className="ml-auto flex items-center gap-2">
        {message && (
          <span role="status" className={cn('text-xs', message.error ? 'text-destructive' : 'text-muted-foreground')}>
            {message.text}
          </span>
        )}
        {state !== 'none' && (
          <>
            <button
              type="button"
              disabled={busy !== null}
              onClick={() => void run('remove')}
              className="flex h-8 items-center gap-1.5 rounded-lg border border-border px-3 text-xs hover:bg-accent disabled:opacity-50"
            >
              {busy === 'remove' && <Loader2 className="h-3 w-3 animate-spin" />}
              Remove from library
            </button>
            <button
              type="button"
              disabled={busy !== null}
              onClick={() => void run('add')}
              className="flex h-8 items-center gap-1.5 rounded-lg bg-foreground px-3 text-xs font-medium text-background hover:bg-foreground/90 disabled:opacity-50"
            >
              {busy === 'add' && <Loader2 className="h-3 w-3 animate-spin" />}
              Add {count} to library
            </button>
          </>
        )}
      </div>
    </div>
  )
}

interface SelectBoxProps {
  state: 'all' | 'some' | 'none'
  className?: string
}

/** Checkbox glyph shared by the bar and the cards. */
export function SelectBox({ state, className }: SelectBoxProps) {
  return (
    <span
      className={cn(
        'flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border transition-colors',
        state === 'none' ? 'border-muted-foreground/50 bg-background/40' : 'border-foreground bg-foreground text-background',
        className,
      )}
    >
      {state === 'all' && <Check className="h-3 w-3" />}
      {state === 'some' && <Minus className="h-3 w-3" />}
    </span>
  )
}
